import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ReceitaBasicInfo from "@/components/receita/ReceitaBasicInfo";
import ReceitaCliente from "@/components/receita/ReceitaCliente";
import ReceitaLaboratorio from "@/components/receita/ReceitaLaboratorio";
import ReceitaProdutos from "@/components/receita/ReceitaProdutos";
import ReceitaPagamentos from "@/components/receita/ReceitaPagamentos";
import ReceitaStatusObs from "@/components/receita/ReceitaStatusObs";
import ReceitaFotos from "@/components/receita/ReceitaFotos";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Redimensiona/comprime a imagem no cliente antes do upload (evita erros de tamanho no proxy)
const compressImage = (file, maxDim = 1600, quality = 0.8) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
        else { width = Math.round((width * maxDim) / height); height = maxDim; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(blob ? new File([blob], "foto.jpg", { type: "image/jpeg" }) : file),
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

let _keySeq = 0;
const genKey = () => `row_${++_keySeq}_${Date.now()}`;
const withKey = (obj) => ({ ...obj, _key: obj._key || genKey() });

// Mantém a data original da venda ao editar (fallback para data_registro, não para hoje)
const dataVendaFrom = (d) =>
  d?.data_venda || (d?.data_registro ? String(d.data_registro).split("T")[0] : new Date().toISOString().split("T")[0]);

const ReceitaForm = ({ initialData, onSubmit, onCancel }) => {
  const { register, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      ...initialData,
      data_venda: dataVendaFrom(initialData),
    },
  });

  const [fotos, setFotos] = useState(initialData?.fotos || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dataVenda, setDataVenda] = useState(dataVendaFrom(initialData));
  const isEditing = !!initialData?.id;

  const [formasPagamento, setFormasPagamento] = useState(
    (initialData?.formas_pagamento || [{ forma: "", valor: "", parcelas: null }]).map(withKey)
  );

  const [produtosAdicionais, setProdutosAdicionais] = useState(
    (initialData?.produtos_adicionais || []).map(withKey)
  );

  const [tipoProduto, setTipoProduto] = useState(initialData?.tipo_produto || "");

  const [catalogo, setCatalogo] = useState([]);
  useEffect(() => {
    axios.get(`${API}/catalogo/produtos`).then((r) => setCatalogo(r.data.produtos || [])).catch(() => {});
  }, []);
  const lensNames = ["Lente Visão Simples", "Lente Multifocal", ...catalogo.filter((c) => c.eh_lente).map((c) => c.nome)];

  const mostrarCamposReceita = lensNames.includes(tipoProduto);

  useEffect(() => {
    if (initialData) {
      reset({
        ...initialData,
        data_venda: dataVendaFrom(initialData),
      });
      setFotos(initialData.fotos || []);
      setDataVenda(dataVendaFrom(initialData));
      if (initialData.formas_pagamento && initialData.formas_pagamento.length > 0) {
        setFormasPagamento(initialData.formas_pagamento.map(withKey));
      } else if (initialData.forma_pagamento) {
        setFormasPagamento([withKey({
          forma: initialData.forma_pagamento,
          valor: initialData.valor_total?.toString() || "",
          parcelas: initialData.parcelas,
        })]);
      }
      if (initialData.tipo_produto) {
        setTipoProduto(initialData.tipo_produto);
      }
      if (initialData.produtos_adicionais && initialData.produtos_adicionais.length > 0) {
        setProdutosAdicionais(initialData.produtos_adicionais.map(withKey));
      }
    }
  }, [initialData, reset]);

  const handleTipoProdutoChange = (value) => {
    setTipoProduto(value);
    setValue("tipo_produto", value);
    // Limpa o estilo ao trocar de tipo (opções diferem entre Armação e Óculos de Sol)
    setValue("estilo_armacao", "");
    setValue("catalogo_produto_id", null);
  };

  const adicionarProduto = () => {
    setProdutosAdicionais([...produtosAdicionais, withKey({
      tipo: "", referencia: "", nome_laboratorio: "", tipo_lente: "", valor: "",
    })]);
  };

  const removerProduto = (index) => {
    setProdutosAdicionais(produtosAdicionais.filter((_, i) => i !== index));
  };

  const atualizarProduto = (index, campo, valor) => {
    const novos = [...produtosAdicionais];
    novos[index] = { ...novos[index], [campo]: valor };
    setProdutosAdicionais(novos);
  };

  const atualizarProdutoCampos = (index, campos) => {
    setProdutosAdicionais((prev) => prev.map((p, i) => (i === index ? { ...p, ...campos } : p)));
  };

  const adicionarFormaPagamento = () => {
    setFormasPagamento([...formasPagamento, withKey({ forma: "", valor: "", parcelas: null })]);
  };

  const removerFormaPagamento = (index) => {
    if (formasPagamento.length > 1) {
      setFormasPagamento(formasPagamento.filter((_, i) => i !== index));
    }
  };

  const atualizarFormaPagamento = (index, campo, valor) => {
    const novas = [...formasPagamento];
    novas[index] = { ...novas[index], [campo]: valor };
    if (campo === "forma" && valor !== "Crédito") {
      novas[index].parcelas = null;
    }
    setFormasPagamento(novas);
  };

  const calcularValorTotal = () => {
    return formasPagamento.reduce((total, fp) => total + (parseFloat(fp.valor) || 0), 0);
  };

  // Soma dos produtos: valor da lente + produtos adicionais
  const valorLente = parseFloat(watch("valor_lente")) || 0;
  const calcularTotalProdutos = () => {
    const somaAdicionais = produtosAdicionais.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0);
    return valorLente + somaAdicionais;
  };
  // Valor total da venda = soma dos produtos (fallback para soma das formas se nao houver produtos com valor)
  const calcularValorVenda = () => {
    const totalProdutos = calcularTotalProdutos();
    return totalProdutos > 0 ? totalProdutos : calcularValorTotal();
  };

  const handleFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (isEditing) {
      setUploading(true);
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append("file", compressed);
      try {
        const res = await axios.post(`${API}/receitas/${initialData.id}/fotos`, formData);
        setFotos((prev) => [...prev, res.data.url]);
        toast.success("Foto anexada!");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erro ao enviar foto");
      } finally {
        setUploading(false);
      }
    } else {
      const compressed = await compressImage(file);
      const preview = URL.createObjectURL(compressed);
      setPendingFiles((prev) => [...prev, { file: compressed, preview }]);
      toast.success("Foto selecionada. Sera enviada ao salvar.");
    }
    e.target.value = "";
  };

  const handleFotoRemove = async (url) => {
    if (!isEditing) return;
    try {
      await axios.delete(`${API}/receitas/${initialData.id}/fotos?url=${encodeURIComponent(url)}`);
      setFotos((prev) => prev.filter((f) => f !== url));
      toast.success("Foto removida");
    } catch {
      toast.error("Erro ao remover foto");
    }
  };

  const removePendingFile = (idx) => {
    setPendingFiles((prev) => {
      URL.revokeObjectURL(prev[idx].preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const onFormSubmit = (data) => {
    const valorTotal = calcularValorVenda();
    const formattedData = {
      ...data,
      valor_lente: data.valor_lente ? parseFloat(data.valor_lente) : null,
      desconto: data.desconto ? parseFloat(data.desconto) : null,
      valor_total: valorTotal,
      formas_pagamento: formasPagamento.filter((fp) => fp.forma && fp.valor).map((fp) => ({
        forma: fp.forma,
        valor: parseFloat(fp.valor) || 0,
        parcelas: fp.parcelas ? parseInt(fp.parcelas) : null,
      })),
      produtos_adicionais: produtosAdicionais.filter((p) => p.tipo).map((p) => ({
        tipo: p.tipo,
        referencia: p.referencia || "",
        nome_laboratorio: p.nome_laboratorio || "",
        tipo_lente: p.tipo_lente || "",
        estilo_armacao: p.estilo_armacao || "",
        descricao_servico: p.descricao_servico || "",
        tipo_acessorio: p.tipo_acessorio || "",
        os: p.os || "",
        cliente_tipo: p.cliente_tipo || "mesmo",
        cliente_nome: p.cliente_tipo === "novo" ? (p.cliente_nome || "") : "",
        cliente_cpf: p.cliente_tipo === "novo" ? (p.cliente_cpf || "") : "",
        cliente_telefone: p.cliente_tipo === "novo" ? (p.cliente_telefone || "") : "",
        od_esf: p.od_esf || "", od_cil: p.od_cil || "", od_eixo: p.od_eixo || "", od_adicao: p.od_adicao || "",
        oe_esf: p.oe_esf || "", oe_cil: p.oe_cil || "", oe_eixo: p.oe_eixo || "", oe_adicao: p.oe_adicao || "",
        dnp_od: p.dnp_od || "", dnp_oe: p.dnp_oe || "", altura_od: p.altura_od || "", altura_oe: p.altura_oe || "",
        valor: parseFloat(p.valor) || 0,
        catalogo_produto_id: p.catalogo_produto_id || null,
      })),
      forma_pagamento: formasPagamento[0]?.forma || null,
      parcelas: formasPagamento[0]?.parcelas || null,
      data_venda: dataVenda,
      fotos: fotos,
    };
    onSubmit(formattedData, pendingFiles.map((p) => p.file));
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8" data-testid="receita-form">
      <ReceitaBasicInfo
        register={register}
        setValue={setValue}
        initialData={initialData}
        dataVenda={dataVenda}
        setDataVenda={setDataVenda}
        tipoProduto={tipoProduto}
        onTipoProdutoChange={handleTipoProdutoChange}
        catalogo={catalogo}
        loja={watch("nome_loja")}
        estiloArmacao={watch("estilo_armacao")}
      />

      <ReceitaCliente register={register} />

      {mostrarCamposReceita && <ReceitaLaboratorio register={register} />}

      <ReceitaProdutos
        produtosAdicionais={produtosAdicionais}
        adicionarProduto={adicionarProduto}
        removerProduto={removerProduto}
        atualizarProduto={atualizarProduto}
        atualizarProdutoCampos={atualizarProdutoCampos}
        loja={watch("nome_loja")}
        catalogo={catalogo}
      />

      <ReceitaPagamentos
        formasPagamento={formasPagamento}
        adicionarFormaPagamento={adicionarFormaPagamento}
        removerFormaPagamento={removerFormaPagamento}
        atualizarFormaPagamento={atualizarFormaPagamento}
        calcularValorTotal={calcularValorTotal}
        totalProdutos={calcularTotalProdutos()}
      />

      <ReceitaStatusObs register={register} setValue={setValue} initialData={initialData} />

      <ReceitaFotos
        fotos={fotos}
        pendingFiles={pendingFiles}
        uploading={uploading}
        isEditing={isEditing}
        handleFotoUpload={handleFotoUpload}
        handleFotoRemove={handleFotoRemove}
        removePendingFile={removePendingFile}
      />

      <div className="flex gap-4 pt-6 border-t border-slate-200">
        <Button type="submit" data-testid="submit-button" className="active:scale-95 transition-transform">Salvar Receita</Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-button" className="active:scale-95 transition-transform">Cancelar</Button>
      </div>
    </form>
  );
};

export default ReceitaForm;
